import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff, Mic, MicOff, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface BoothPreJoinModalProps {
  isOpen: boolean;
  boothName: string;
  stream: MediaStream | null;
  isInitializing: boolean;
  permissionDenied: boolean;
  error: string | null;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onRequestPermissions: () => Promise<boolean>;
  onJoin: () => void;
  onCancel: () => void;
}

const BoothPreJoinModal: React.FC<BoothPreJoinModalProps> = ({
  isOpen,
  boothName,
  stream,
  isInitializing,
  permissionDenied,
  error,
  isVideoEnabled,
  isAudioEnabled,
  onToggleVideo,
  onToggleAudio,
  onRequestPermissions,
  onJoin,
  onCancel,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play()
        .then(() => setVideoPlaying(true))
        .catch(err => console.error("[PreJoin] Video play failed:", err));
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  // Request permissions automatically when modal opens or stream is lost
  useEffect(() => {
    if (isOpen && !stream && !isInitializing) {
      console.log("[PreJoin] Auto-requesting permissions");
      onRequestPermissions();
    }
  }, [isOpen, stream, isInitializing, onRequestPermissions]);

  const canJoin = stream && !isInitializing;

  return (
    <Dialog open={isOpen} onOpenChange={() => onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Join "{boothName}"</DialogTitle>
          <DialogDescription>
            Preview your camera before entering the live booth
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera Preview */}
          <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
            {isInitializing ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary mb-2" />
                <p className="text-sm text-muted-foreground">Requesting camera access...</p>
              </div>
            ) : permissionDenied || error ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                <AlertCircle className="w-10 h-10 text-destructive mb-2" />
                <p className="text-sm text-center text-destructive font-medium">
                  {error || "Camera access denied"}
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={onRequestPermissions}
                >
                  Try Again
                </Button>
              </div>
            ) : stream && isVideoEnabled ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                disablePictureInPicture
                className="w-full h-full object-cover mirror"
                style={{ transform: "scaleX(-1)" }}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted">
                <CameraOff className="w-10 h-10 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Camera is off</p>
              </div>
            )}

            {/* Video status badge */}
            {stream && videoPlaying && isVideoEnabled && (
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-green-500/80 text-white text-xs font-medium">
                Preview Active
              </div>
            )}
          </div>

          {/* Controls */}
          {stream && (
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={onToggleVideo}
                className={cn(
                  "rounded-full w-12 h-12",
                  !isVideoEnabled && "bg-red-500/10 border-red-500/50 text-red-500"
                )}
              >
                {isVideoEnabled ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />}
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={onToggleAudio}
                className={cn(
                  "rounded-full w-12 h-12",
                  !isAudioEnabled && "bg-red-500/10 border-red-500/50 text-red-500"
                )}
              >
                {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </Button>
            </div>
          )}

          {/* Join Button */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500"
              onClick={onJoin}
              disabled={!canJoin}
            >
              Join Live
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Your camera will be visible to everyone in the booth
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoothPreJoinModal;
